
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GameFAQ = () => {
  const faqs = [
    {
      question: "What is Time Shooter 3 Unblocked?",
      answer: "Time Shooter 3 Unblocked is a free online first-person shooter game where players take on the role of a SWAT operative completing various tactical missions. The game's unique feature is the ability to slow down time, allowing for strategic combat advantages and precision shooting. It's 'unblocked' meaning it can be played on browsers even in locations where gaming sites might typically be restricted."
    },
    {
      question: "Do I need to download anything to play Time Shooter 3 Unblocked?",
      answer: "No, Time Shooter 3 Unblocked is a browser-based game that requires no downloads or installations. You can play it directly in your web browser using just your keyboard and mouse. The game is optimized to run smoothly on most modern browsers without requiring any additional software or plugins."
    },
    {
      question: "How do I use the time manipulation mechanic?",
      answer: "The time manipulation mechanic is activated by pressing the 'T' key (by default). This slows down time around you, allowing you to aim more precisely, dodge bullets, and plan strategic moves. Your time gauge (visible on screen) will deplete while using this ability and will recharge when not in use. Use this ability strategically as it's limited by the time gauge."
    },
    {
      question: "Are there different weapons in Time Shooter 3 Unblocked?",
      answer: "Yes, Time Shooter 3 Unblocked features a variety of weapons including assault rifles, shotguns, sniper rifles, SMGs, pistols, and special tactical equipment. Each weapon has different characteristics like damage, accuracy, range, and firing rate. You can unlock additional weapons by completing missions and achieving specific objectives throughout the game."
    },
    {
      question: "How many missions are there in Time Shooter 3 Unblocked?",
      answer: "Time Shooter 3 Unblocked contains multiple missions across different difficulty levels. Starting with a training mission to learn the basics, you'll progress through various scenarios including hostage rescue, bomb defusal, and elimination objectives. Each mission presents unique challenges and environments, from urban settings to industrial complexes."
    },
    {
      question: "Can I customize the controls in Time Shooter 3 Unblocked?",
      answer: "Yes, Time Shooter 3 Unblocked allows for control customization through the settings menu. You can adjust key bindings, mouse sensitivity, and other control options to match your preferences. This customization helps ensure comfortable gameplay regardless of your previous FPS experience."
    },
    {
      question: "Is Time Shooter 3 Unblocked multiplayer or single-player?",
      answer: "Time Shooter 3 Unblocked is primarily a single-player experience focusing on tactical missions with the time manipulation mechanic. The game emphasizes strategic planning, precision shooting, and mastering the time slowdown ability rather than multiplayer competition."
    },
    {
      question: "How do I improve my performance in Time Shooter 3 Unblocked?",
      answer: "To improve your performance, focus on mastering the time manipulation mechanic, aim for headshots to conserve ammunition, use cover effectively, and learn each weapon's characteristics. Practice the advanced techniques like 'time pulsing' and strategic reloading. Each mission can be replayed to perfect your approach and achieve better completion times and accuracy ratings."
    },
    {
      question: "Why does the game lag sometimes and how can I fix it?",
      answer: "Browser-based games like Time Shooter 3 Unblocked may experience performance issues depending on your computer specifications and browser configuration. To improve performance: close unnecessary browser tabs and applications, use a modern browser like Chrome or Firefox, lower the game's quality settings if available, ensure your browser is updated, and try disabling browser extensions that might interfere with the game's performance."
    },
    {
      question: "Is Time Shooter 3 Unblocked suitable for all ages?",
      answer: "Time Shooter 3 Unblocked contains realistic depictions of firearms and tactical combat scenarios. While less graphic than many modern FPS games, it still involves shooting enemies and missions with mature themes. The game is generally considered appropriate for teens and adults rather than young children."
    }
  ];

  return (
    <section className="my-16" id="faq">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 text-center text-purple-300">Frequently Asked Questions</h2>
        
        <div className="bg-indigo-900/30 rounded-xl overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-indigo-800/50">
                <AccordionTrigger className="px-6 py-4 flex justify-between items-center w-full text-left font-semibold text-lg text-purple-300 hover:bg-indigo-800/20">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-100">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        
        <div className="mt-10 bg-gradient-to-r from-indigo-900/50 to-purple-900/50 p-6 rounded-xl text-center">
          <h3 className="text-xl font-bold mb-4 text-purple-300">Still Have Questions?</h3>
          <p className="text-indigo-100 mb-6">
            Can't find the answer you're looking for? The best way to learn more is to dive in and play Time Shooter 3 Unblocked yourself!
          </p>
          <a href="#play-now" className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105">
            Play Now and Discover More
          </a>
        </div>
      </div>
    </section>
  );
};

export default GameFAQ;
